import React from 'react'
import Modal from 'shared/ui/Modal/Modal'
import Button from 'shared/ui/Button/Button'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg'
import { ReactComponent as PersonAddIcon } from 'assets/icons/person_add.svg'
import { ReactComponent as PersonIcon } from 'assets/icons/person.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash-2.svg'
import { ReactComponent as StarIcon } from 'assets/icons/check_star.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import InlineErrorBanner from '../InlineErrorBanner/InlineErrorBanner'
import ContactFields from './ContactFields'
import useContactModal, { firstName } from './hooks/useContactModal'
import s from './ContactModal.module.scss'

export const ContactModal = ({
	contactDraft,
	closeContact,
	updateContactField,
	updateAddFormField,
	submitContact,
	deleteContact,
	openAddContact,
	contactFormError,
	contactSaving,
}) => {
	const {
		isAddMode,
		contacts,
		selectedId,
		setSelectedId,
		addForm,
		activeContact,
		onFieldChange,
		canSave,
		handleSubmit,
		switchToAddContact,
		requestDelete,
		cancelDelete,
		confirmDelete,
		deleteConfirmOpen,
	} = useContactModal({
		contactDraft,
		updateContactField,
		updateAddFormField,
		submitContact,
		deleteContact,
		contactSaving,
		openAddContact,
	})

	return (
		<>
			<Modal
				isOpen
				onClose={closeContact}
				title={isAddMode ? 'Add contact' : 'Edit parent contacts'}
				iconTitle={isAddMode ? PersonAddIcon : PenIcon}
				subtitle="These contacts receive the enabled parent updates."
				ariaLabel={
					isAddMode
						? `Add contact for ${contactDraft.studentName}`
						: `Edit parent contacts for ${contactDraft.studentName}`
				}
				width={560}
				maxHeight={720}
				compact
				closeButtonClassName={s.closeButton}
				closeOnBackdrop={!deleteConfirmOpen}
				closeOnEsc={!deleteConfirmOpen}
			>
				<form className={s.form} onSubmit={handleSubmit}>
					<div className={s.scroll}>
						{contactFormError ? (
							<InlineErrorBanner message={contactFormError} />
						) : null}

						{!isAddMode ? (
							<div className={s.chips} role="tablist" aria-label="Contacts">
								{contacts.map(contact => {
									const isActive = contact.id === selectedId
									return (
										<button
											key={contact.id}
											type="button"
											role="tab"
											aria-selected={isActive}
											className={`${s.chip} ${isActive ? s.chipActive : ''}`.trim()}
											onClick={() => setSelectedId(contact.id)}
										>
											<span className={s.chipIcon} aria-hidden="true">
												<PersonIcon />
											</span>
											<span className={s.chipLabel}>
												{firstName(contact.contact_name)}
											</span>
											{contact.is_primary ? (
												<span className={s.chipCrown} aria-label="Primary">
													<StarIcon />
												</span>
											) : null}
										</button>
									)
								})}
								<button
									type="button"
									className={`${s.chip} ${s.chipAdd}`.trim()}
									onClick={switchToAddContact}
									disabled={contactSaving}
								>
									<span className={s.chipIcon} aria-hidden="true">
										<PlusIcon />
									</span>
									<span>Add contact</span>
								</button>
							</div>
						) : null}

						{!isAddMode && activeContact ? (
							<div className={s.identity}>
								<InitialAvatar name={activeContact.contact_name} size={40} />
								<span className={s.identity__name}>
									{activeContact.contact_name || 'Contact'}
								</span>
								{activeContact.is_primary ? (
									<span className={s.primaryBadge}>Primary contact</span>
								) : null}
							</div>
						) : null}

						{isAddMode ? (
							<div className={s.studentMeta}>
								<InitialAvatar
									name={contactDraft.studentName}
									avatarKey={contactDraft.avatarKey}
									size={36}
								/>
								<span className={s.studentMetaCopy}>
									<span className={s.studentMetaName}>
										{contactDraft.studentName}
									</span>
									<span className={s.studentMetaGrade}>{contactDraft.grade}</span>
								</span>
							</div>
						) : null}

						<div className={s.fields}>
							{isAddMode && addForm ? (
								<ContactFields
									values={addForm}
									onFieldChange={onFieldChange}
									nameId="contact-name"
									emailId="contact-email"
									typeId="contact-type"
								/>
							) : activeContact ? (
								<ContactFields
									values={activeContact}
									onFieldChange={onFieldChange}
									nameId={`edit-contact-name-${activeContact.id}`}
									emailId={`edit-contact-email-${activeContact.id}`}
									typeId={`edit-contact-type-${activeContact.id}`}
								/>
							) : null}
						</div>
					</div>

					<div className={s.footer}>
						{!isAddMode && activeContact ? (
							<button
								type="button"
								className={s.delete}
								onClick={requestDelete}
								disabled={contactSaving}
							>
								<span className={s.deleteIcon} aria-hidden="true">
									<TrashIcon />
								</span>
								Delete contact
							</button>
						) : (
							<span className={s.footerSpacer} />
						)}

						<div className={s.footerActions}>
							<Button variant="secondary" onClick={closeContact}>
								Cancel
							</Button>
							<Button type="submit" variant="primary" disabled={!canSave}>
								{contactSaving
									? 'Saving…'
									: isAddMode
										? 'Save contact'
										: 'Save changes'}
							</Button>
						</div>
					</div>
				</form>
			</Modal>

			<Modal
				isOpen={deleteConfirmOpen}
				onClose={cancelDelete}
				title="Delete parent contact?"
				subtitle={
					activeContact
						? `Are you sure you want to delete ${
								activeContact.contact_name ||
								activeContact.email ||
								'this parent'
							} from ${contactDraft.studentName}?`
						: 'Are you sure you want to delete this parent from this student?'
				}
				ariaLabel="Delete parent contact confirmation"
				width={480}
				compact
				closeOnBackdrop={!contactSaving}
				closeOnEsc={!contactSaving}
				footerButtons={[
					{
						id: 'cancel',
						text: 'Cancel',
						variant: 'secondary',
						disabled: contactSaving,
						onClick: cancelDelete,
					},
					{
						id: 'delete',
						text: contactSaving ? 'Deleting…' : 'Delete contact',
						variant: 'primary',
						disabled: contactSaving,
						onClick: confirmDelete,
					},
				]}
			/>
		</>
	)
}

export default ContactModal
